import { useLocaleStoreWithOut } from '@/store/modules/locale'
import dayjs from 'dayjs'

export const getNumberRangeByParams = (arr: any) => {
  let result = ''
  if (arr && arr.length === 2) {
    const [min, max] = arr
    if (min !== undefined || max !== undefined) {
      result = `${min ?? ''},${max ?? ''}`
    }
  }

  return result
}

export const getTimestampRangeByParams = (arr: any) => {
  let result = ''
  if (arr && arr.length === 2) {
    // 返回时间戳范围
    result = `${dayjs(arr[0]).startOf('day').format('X')},${dayjs(arr[1]).endOf('day').format('X')}`
  }
  return result
}

export const isRequired = (value: any) => {
  return value === undefined || value === null || value === ''
}

export const $percent = (num: number = 0, precision = 2) => {
  return (num * 100).toFixed(precision) + '%'
}

export const $numeral = (num: number | string | bigint = 0, sys = '0,0.0000') => {
  // 将输入转换为字符串
  const numStr = '' + num

  // 分割整数部分和小数部分
  const [integerPart, fractionalPart = ''] = numStr.split('.')

  // 处理整数部分
  const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',')

  // 获取小数位数
  const decimalPlaces = sys.split('.')[1] ? sys.split('.')[1].length : 0

  // 处理小数部分
  const formattedFractional = fractionalPart.slice(0, decimalPlaces).padEnd(decimalPlaces, '0')

  // 组合结果
  return decimalPlaces > 0 ? `${formattedInteger}.${formattedFractional}` : formattedInteger
}

/**
 *
 * @param component 需要注册的组件
 * @param alias 组件别名
 * @returns any
 */
export const withInstall = <T>(component: T, alias?: string) => {
  const comp = component as any
  comp.install = (app: any) => {
    app.component(comp.name || comp.displayName, component)
    if (alias) {
      app.config.globalProperties[alias] = component
    }
  }
  return component as T & Plugin
}

/**
 * @param str 需要转下划线的驼峰字符串
 * @returns 字符串下划线
 */
export const humpToUnderline = (str: string): string => {
  return str.replace(/([A-Z])/g, '-$1').toLowerCase()
}

/**
 * @param str 需要转驼峰的下划线字符串
 * @returns 字符串驼峰
 */
export const underlineToHump = (str: string): string => {
  if (!str) return ''
  return str.replace(/\-(\w)/g, (_, letter: string) => {
    return letter.toUpperCase()
  })
}

/**
 * 驼峰转横杠
 */
export const humpToDash = (str: string): string => {
  return str.replace(/([A-Z])/g, '-$1').toLowerCase()
}

export const setCssVar = (prop: string, val: any, dom = document.documentElement) => {
  dom.style.setProperty(prop, val)
}

export const getCssVar = (prop: string, dom = document.documentElement) => {
  return getComputedStyle(dom).getPropertyValue(prop)
}

/**
 * 查找数组对象的某个下标
 * @param {Array} ary 查找的数组
 * @param {Functon} fn 判断的方法
 */
export const findIndex = <T = Recordable>(ary: Array<T>, fn: Fn): number => {
  if (ary.findIndex) {
    return ary.findIndex(fn)
  }
  let index = -1
  ary.some((item: T, i: number, ary: Array<T>) => {
    const ret: T = fn(item, i, ary)
    if (ret) {
      index = i
      return ret
    }
  })
  return index
}

export const trim = (str: string) => {
  return str.replace(/(^\s*)|(\s*$)/g, '')
}

/**
 * @param {Date | number | string} time 需要转换的时间
 * @param {String} fmt 需要转换的格式 如 yyyy-MM-dd、yyyy-MM-dd HH:mm:ss
 */
export function formatTime(time: Date | number | string, fmt: string) {
  if (!time) return ''
  else {
    const date = new Date(time)
    const o = {
      'M+': date.getMonth() + 1,
      'd+': date.getDate(),
      'H+': date.getHours(),
      'm+': date.getMinutes(),
      's+': date.getSeconds(),
      'q+': Math.floor((date.getMonth() + 3) / 3),
      S: date.getMilliseconds()
    }
    if (/(y+)/.test(fmt)) {
      fmt = fmt.replace(RegExp.$1, (date.getFullYear() + '').substr(4 - RegExp.$1.length))
    }
    for (const k in o) {
      if (new RegExp('(' + k + ')').test(fmt)) {
        fmt = fmt.replace(
          RegExp.$1,
          RegExp.$1.length === 1 ? o[k] : ('00' + o[k]).substr(('' + o[k]).length)
        )
      }
    }
    return fmt
  }
}

/**
 * 生成随机字符串
 */
export function toAnyString() {
  const str: string = 'xxxxx-xxxxx-4xxxx-yxxxx-xxxxx'.replace(/[xy]/g, (c: string) => {
    const r: number = (Math.random() * 16) | 0
    const v: number = c === 'x' ? r : (r & 0x3) | 0x8
    return v.toString()
  })
  return str
}

/**
 * 首字母大写
 */
export function firstUpperCase(str: string) {
  return str.toLowerCase().replace(/( |^)[a-z]/g, (L) => L.toUpperCase())
}

/**
 * 把对象转为formData
 */
export function objToFormData(obj: Recordable) {
  const formData = new FormData()
  Object.keys(obj).forEach((key) => {
    formData.append(key, obj[key])
  })
  return formData
}

/**
 * 获取对象的值
 * @param target 目标对象
 * @param path 路径
 * @param defaultValue 默认值
 * @returns 值
 */
export function getValue(target = {}, path = '', defaultValue: any = undefined) {
  const isNil = (value: any) => value === null || value === undefined || value === ''
  const value = path
    .split('.')
    .reduce(
      (last: any, key: any) => (key && last && !isNil(last[key]) ? last[key] : undefined),
      target
    )

  return isNil(value) ? defaultValue : value
}

/**
 * 根据当前语言显示标签
 * @param obj 对象
 * @returns 标签
 */
export const showLangLabel = (obj: Recordable, defaultValue: any = '--') => {
  const localeStore = useLocaleStoreWithOut()
  const locale = localeStore.getCurrentLocale

  return getValue(obj, 'en-AU', undefined) || defaultValue
}

export const formatDate = (date: any, showDetail = true) => {
  if (!date) return '--'
  if (typeof date === 'number') {
    return showDetail
      ? dayjs(date * 1000).format('YYYY/MM/DD HH:mm:ss')
      : dayjs(date * 1000).format('YYYY/MM/DD')
  } else {
    return showDetail ? dayjs(date).format('YYYY/MM/DD HH:mm:ss') : dayjs(date).format('YYYY/MM/DD')
  }
}
